<template>
    <MainContent
        title="Listado de equipos"
        :value="filters"
        :meta="equipos?.meta || {}"
        @input="val => Object.assign(filters, val)"
    >
        <template #header>
            <!-- Skeleton Card solo en primera carga -->
            <template v-if="!hasLoadedOnce && loading">
                <Card customClasses="tw:mb-4 tw:px-4 tw:py-4">
                    <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                        <template #skeleton>
                            <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:md:gap-14 tw:justify-between">
                                <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                                <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:w-full tw:justify-end">
                                    <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:md:w-[133px] tw:rounded-lg" />
                                    <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:md:w-[204px] tw:rounded-lg" />
                                </div>
                            </div>
                        </template>
                    </Skeleton>
                </Card>
            </template>

            <!-- Real Card when loaded -->
            <template v-else>
                <Card customClasses="tw:mb-4 tw:px-4 tw:py-4">
                    <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:md:gap-14 tw:justify-between tw:items-center">
                        <div class="tw:w-full">
                            <SearchComponent
                                :value="filters.search"
                                placeholder="Buscar"
                                @input="val => filters.search = val"
                            />
                        </div>
                        <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:w-full tw:sm:justify-end">
                            <Button
                                class="tw:min-w-fit tw:justify-center"
                                as="router-link"
                                :to="{ name: 'back.bases.equipos.crear', params: { lista_id: listaId } }"
                                icon="fas fa-plus"
                                customClass="tw:flex tw:justify-center"
                            >
                                Crear
                            </Button>
                            <Button
                                class="tw:min-w-fit tw:justify-center"
                                v-if="hasEquiposAnterior"
                                @click="openCargarAnterior"
                            >
                                Cargar equipos temp. anterior
                            </Button>
                        </div>
                    </div>
                </Card>
            </template>
        </template>

        <EquiposList
            :equipos="equipos?.data || []"
            :loading="loading"
            :columns="columns"
            :lista-id="listaId"
            @borrar="onBorrar"
            @descargar="onDescargarListado"
        />

        <div v-if="!hasLoadedOnce && loading" class="tw:mt-6">
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200 tw:p-4">
                <div class="tw:flex tw:items-center tw:justify-between">
                    <SkeletonItem height="tw:h-6" customClass="tw:w-40 tw:rounded" />
                    <div class="tw:flex tw:gap-1">
                        <SkeletonItem height="tw:h-8" customClass="tw:w-8 tw:rounded" v-for="n in 3" :key="'pag-' + n" />
                    </div>
                </div>
            </div>
        </div>

        <CargarEquiposAnterior
            :lista-id="listaId"
            @imported="onEquiposImported"
        />

        <BorrarModalComponent
            v-if="borrarEquipoId !== null"
            :name="'borrar-' + borrarEquipoId"
            :title="'Borrar equipo'"
            @close="borrarEquipoId = null"
            @confirm="confirmarBorrar"
        >
            ¿Está segur@ de borrar este equipo?
        </BorrarModalComponent>
    </MainContent>
</template>

<script setup>
    import { ref, watch, nextTick, getCurrentInstance } from 'vue';

    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import EquiposList from '@/modules/admin/bases/equipos/components/EquiposList.vue';
    import CargarEquiposAnterior from '@/modules/admin/bases/equipos/CargarEquiposAnterior.vue';
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    import { useEquiposIndex } from '@/composables/admin/bases/equipos/useEquiposIndex';
    import { useToast } from '@/composables/common/useToast';
    import store from '@/store';
    import { config } from '@/config.js';

    const toast = useToast();
    const instance = getCurrentInstance();
    const vm = instance?.proxy;

    const {
        equipos,
        loading,
        filters,
        hasEquiposAnterior,
        listaId,
        columns,
        fetch,
        borrar,
    } = useEquiposIndex();

    const hasLoadedOnce = ref(false);
    watch(equipos, val => {
        if (val?.data?.length) hasLoadedOnce.value = true;
    });

    const borrarEquipoId = ref(null);

    function openCargarAnterior() {
        vm.$modal.show('cargarEquiposAnterior');
    }

    function onBorrar(id) {
        borrarEquipoId.value = id;
        nextTick(() => {
            vm.$modal.show('borrar-' + id);
        });
    }

    function confirmarBorrar() {
        const id = borrarEquipoId.value;
        if (id) {
            borrar(id);
        }
        borrarEquipoId.value = null;
    }

    function onEquiposImported() {
        toast.success('Equipos cargados correctamente');
        fetch();
    }

    async function onDescargarListado(equipoId) {
        try {
            await store.dispatch(
                config.getVuexAlias('back.bases.equipos.descargar-listado'),
                {
                    lista_id: listaId.value,
                    equipo_id: equipoId,
                    filters: {
                        tid: localStorage.getItem('hash_teatro')
                    }
                }
            );
            toast.info('Generando listado de equipo');
        } catch (error) {
            toast.error('Error al generar el listado');
        }
    }

</script>
